import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Import components
const LoginPage = () => import('@/pages/LoginPage.vue')
const RegisterPage = () => import('@/pages/RegisterPage.vue')
const AdminLayout = () => import('@/components/layout/AdminLayout.vue')
const FacebookOAuthCallbackPage = () => import('@/pages/FacebookOAuthCallbackPage.vue')
const PrivacyPolicyPage = () => import('@/pages/PrivacyPolicyPage.vue')
const TermsOfServicePage = () => import('@/pages/TermsOfServicePage.vue')
const ContactPage = () => import('@/pages/ContactPage.vue')

// Admin pages
const DashboardPage = () => import('@/pages/DashboardPage.vue')
const FormRequestPage = () => import('@/pages/FormRequestPage.vue')
const AdAccountPage = () => import('@/pages/AdAccountPage.vue')
const ViaFacebookPage = () => import('@/pages/ViaFacebookPage.vue')
const BillingHistoryPage = () => import('@/pages/BillingHistoryPage.vue')
const CampaignPage = () => import('@/pages/CampaignPage.vue')
const UserManagementPage = () => import('@/pages/UserManagementPage.vue')
const RolePermissionPage = () => import('@/pages/RolePermissionPage.vue')
const BankBalancePage = () => import('@/pages/BankBalancePage.vue')
const TelegramCompanyPage = () => import('@/pages/TelegramCompanyPage.vue')
const TelegramGroupPage = () => import('@/pages/TelegramGroupPage.vue')
const TelegramProductivityReportPage = () => import('@/pages/TelegramProductivityReportPage.vue')
const TelegramStaffDetailReportPage = () => import('@/pages/TelegramStaffDetailReportPage.vue')
const CustomerMessagesByHourPage = () => import('@/pages/CustomerMessagesByHourPage.vue')
const RentalCustomersPage = () => import('@/pages/RentalCustomersPage.vue')
const BankInvoicePage = () => import('@/pages/BankInvoicePage.vue')
const PaymentInvoiceManagementPage = () => import('@/pages/PaymentInvoiceManagementPage.vue')
const PaymentCardPage = () => import('@/pages/PaymentCardPage.vue')
const CurrencyExchangePage = () => import('@/pages/CurrencyExchangePage.vue')
const AssetsPage = () => import('@/pages/AssetsPage.vue')
const FinancialDashboardPage = () => import('@/pages/FinancialDashboardPage.vue')
const TagSettingsPage = () => import('@/pages/TagSettingsPage.vue')
const ShieldPage = () => import('@/pages/ShieldPage.vue')
const CustomerSheetPage = () => import('@/pages/CustomerSheetPage.vue')
const RunCommandPage = () => import('@/pages/RunCommandPage.vue')


const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      requiresGuest: true,
      title: 'Sign in'
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: {
      requiresGuest: true,
      title: 'Create account'
    }
  },
  {
    path: '/facebook-oauth-callback',
    name: 'FacebookOAuthCallback',
    component: FacebookOAuthCallbackPage,
    meta: { 
      title: 'Facebook OAuth',
      requiresAuth: false
    }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyPage,
    meta: { 
      requiresGuest: false,
      requiresAuth: false,
      title: 'Privacy policy'
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfServicePage,
    meta: { 
      requiresAuth: false,
      title: 'Terms of service'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: ContactPage,
    meta: { 
      requiresAuth: false,
      title: 'Contact'
    }
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { 
      requiresAuth: true 
    },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { 
          title: 'Overview',
          permission: 'view-dashboard'
        }
      },
      {
        path: 'form-request',
        name: 'FormRequest',
        component: FormRequestPage,
        meta: { 
          title: 'Form request'
        }
      },
      {
        path: 'assets',
        name: 'Assets',
        component: AssetsPage,
        meta: { 
          title: 'Asset management',
          permission: 'view-facebook-via'
        }
      },
      {
        path: 'ad-accounts',
        name: 'AdAccounts',
        component: AdAccountPage,
        meta: { 
          title: 'Ad accounts',
          permission: 'view-ad-accounts'
        }
      },
      {
        path: 'via-facebook',
        name: 'ViaFacebook',
        component: ViaFacebookPage,
        meta: { 
          title: 'Via Facebook',
          permission: 'view-via-facebook'
        }
      },
      {
        path: 'billing-history',
        name: 'BillingHistory',
        component: BillingHistoryPage,
        meta: { 
          title: 'Billing history',
          permission: 'view-billing'
        }
      },
      {
        path: 'campaigns',
        name: 'Campaigns',
        component: CampaignPage,
        meta: { 
          title: 'Ad campaigns',
          permission: 'view-campaigns'
        }
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagementPage,
        meta: { 
          title: 'User management',
          permission: 'view-users'
        }
      },
      {
        path: 'roles',
        name: 'RolePermission',
        component: RolePermissionPage,
        meta: { 
          title: 'Roles & permissions',
          permission: 'view-roles'
        }
      },
      {
        path: 'bank-balance',
        name: 'BankBalance',
        component: BankBalancePage,
        meta: { 
          title: 'Bank balances',
          permission: 'view-bank'
        }
      },
      {
        path: 'telegram-company',
        name: 'TelegramCompany',
        component: TelegramCompanyPage,
        meta: { 
          title: 'Telegram companies',
          permission: 'view-telegram'
        }
      },
      {
        path: 'telegram-groups/:companyId',
        name: 'TelegramGroup',
        component: TelegramGroupPage,
        meta: { 
          title: 'Group Telegram',
          permission: 'view-telegram'
        }
      },
      {
        path: 'telegram-productivity-report',
        name: 'TelegramProductivityReport',
        component: TelegramProductivityReportPage,
        meta: { 
          title: 'Telegram productivity report',
          permission: 'view-telegram'
        }
      },
      {
        path: 'telegram-staff-detail',
        name: 'TelegramStaffDetail',
        component: TelegramStaffDetailReportPage,
        meta: { 
          title: 'Staff productivity detail',
          permission: 'view-telegram'
        }
      },
      {
        path: 'customer-messages-by-hour',
        name: 'CustomerMessagesByHour',
        component: CustomerMessagesByHourPage,
        meta: { 
          title: 'Customer messages by hour',
          permission: 'view-telegram'
        }
      },
      {
        path: 'account-spend',
        name: 'RentalCustomers',
        component: RentalCustomersPage,
        meta: { 
          title: 'Rental ad account customers',
          permission: 'view-rental'
        }
      },
      {
        path: 'bank-invoices',
        name: 'BankInvoice',
        component: BankInvoicePage,
        meta: { 
          title: 'Bank invoices',
          permission: 'view-bank-invoices'
        }
      },
      {
        path: 'payment-invoices',
        name: 'PaymentInvoiceManagement',
        component: PaymentInvoiceManagementPage,
        meta: { 
          title: 'Payment invoice management',
          permission: 'view-payment-invoices'
        }
      },
      {
        path: 'payment-cards',
        name: 'PaymentCards',
        component: PaymentCardPage,
        meta: {
          title: 'Payment cards',
          permission: 'card-group.view'
        }
      },
      {
        path: 'financial-dashboard',
        name: 'FinancialDashboard',
        component: FinancialDashboardPage,
        meta: {
          title: 'Finance & payments',
          permission: 'view-financial-dashboard'
        }
      },
      {
        path: 'currency-exchange',
        name: 'CurrencyExchange',
        component: CurrencyExchangePage,
        meta: { 
          title: 'Currency exchange',
          permission: 'view-currency-exchange'
        }
      },
      {
        path: 'tag-settings',
        name: 'TagSettings',
        component: TagSettingsPage,
        meta: {
          title: 'Tag settings'
        }
      },
      {
        path: 'shields',
        name: 'Shield',
        component: ShieldPage,
        meta: {
          title: 'Shield protection'
        }
      },
      {
        path: 'excel-sheets',
        name: 'CustomerSheet',
        component: CustomerSheetPage,
        meta: {
          title: 'Customer sheets'
        }
      },
      {
        path: 'run-command',
        name: 'RunCommand',
        component: RunCommandPage,
        meta: {
          title: 'Run command',
          permission: 'run-admin-commands'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  // Set page title
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - ${import.meta.env.VITE_APP_TITLE || 'Ads Management'}`
  }

  // Check authentication requirement
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isLoggedIn) {
      next({ 
        path: '/login', 
        query: { redirect: to.fullPath } 
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

// Handle navigation errors
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
